<template>
  <a-dropdown
    v-model:open="dropdownVisible"
    :trigger="['click']"
    placement="bottomLeft"
    overlayClassName="add_property_dropdown"
    @update:open="handleDropdownChange"
  >
    <a-tooltip title="Add Property" placement="topRight">
      <ThemeImage
        alt="add_property"
        class="size-4 cursor-pointer"
        :icon="imageNames.PLUS_GREY_THIN"
      />
    </a-tooltip>

    <template #overlay>
      <div class="add_property_dropdown_content" @wheel.stop @scroll.stop>
        <div class="w-[320px] flex flex-col text-left">
          <a-form
            ref="formRef"
            :model="formState"
            layout="vertical"
            @submit.prevent
          >
            <a-form-item
              class="m-2"
              name="name"
              :rules="nameRules"
              :validate-trigger="['blur', 'change']"
            >
              <a-input
                v-model:value="formState.name"
                class="input_medium"
                placeholder="Enter property name"
                @pressEnter="trySubmit"
              />
            </a-form-item>
            <div :class="{ border_bottom: true }"></div>
            <div class="flex flex-col py-2 gap-2" @wheel.stop @scroll.stop>
              <span class="secondary_color text-[13px] px-5">
                CHOOSE A PROPERTY TYPE
              </span>
              <div
                class="grid grid-cols-2 gap-2 px-2 items-start max-h-[300px] overflow-y-auto custom_scrollbar"
                @wheel.stop
                @scroll.stop
              >
                <div
                  v-for="opt in propertyTypeOptions"
                  :key="opt.value"
                  class="flex items-center gap-2 cursor-pointer dropdown_item truncate"
                  @click="selectType(opt.value)"
                >
                  <ThemeImage
                    v-if="opt.icon"
                    :icon="opt.icon"
                    alt="type"
                    class="size-3.5"
                  />
                  <span class="truncate" :title="opt.label">{{ opt.label }}</span>
                  <a-tooltip
                    v-if="opt.value === 'number'"
                    placement="right"
                    title="Numbers support up to 16 significant digits, with exact integer values guaranteed only up to 9,007,199,254,740,991. Larger values may be rounded. Use the Text format to ensure full accuracy."
                    @click.stop
                  >
                    <ThemeImage
                      class="size-3 flex-shrink-0"
                      :icon="imageNames.INFO_GREY"
                      alt="info_icon"
                    />
                  </a-tooltip>
                </div>
              </div>
            </div>
          </a-form>
        </div>
      </div>
    </template>
  </a-dropdown>
</template>

<script setup>
import ThemeImage from "@/src/components/ThemeImage.vue";
import { imageNames } from "@/src/constants/imageConstants.js";
import { computed, ref } from "vue";
import { message } from "ant-design-vue";
import CRMService from "@/src/api/CRMService.js";
import TicketsService from "@/src/api/TicketsService.js";
import ProjectService from "@/src/api/ProjectService.js";
import {
  customObjectAttributeTypes,
  excludedFormFields,
  ATTRIBUTE_TYPES,
  EXCLUDED_ATTRIBUTE_TYPES_FOR_TASKS_TICKETS,
} from "@/src/constants/crmConstants.js";
import { standardObjects } from "@/src/constants/objectConstants.js";
import {
  checkIfFirstTextProperty,
  setPropertyAsPrimary,
  addNewAttributeToActiveView,
} from "@/src/utils/dynamicCRMViewUtils.js";
import { generateSlugFromName } from "@/src/utils/entityUtils.js";
import { entityTypes } from "@/src/constants/entityTypeConstants";
import { useStore } from "vuex";
const store = useStore();

const props = defineProps({
  entityType: {
    type: String,
    required: true,
  },
  projectId: {
    type: [String, Number],
    default: null,
  },
  service: {
    type: String,
    default: null,
  },
});

const emit = defineEmits(["property-added"]);

const formState = ref({
  name: "",
  type: "",
});

const submitting = ref(false);
const formRef = ref(null);
const dropdownVisible = ref(false);


const isTickets = computed(() => props.entityType === "tickets");
const isTasks = computed(() => props.entityType === "tasks");
const isStandardObject = computed(() =>
  standardObjects.some((obj) => obj.slug === props.entityType)
);

const propertyTypeOptions = computed(() => {
  if (isTickets.value || isTasks.value) {
    return customObjectAttributeTypes.filter(
      (type) => !EXCLUDED_ATTRIBUTE_TYPES_FOR_TASKS_TICKETS.includes(type.value)
    );
  }
  return customObjectAttributeTypes;
});

const trimmedName = computed(() => formState.value.name.trim());
const canSubmit = computed(
  () => trimmedName.value.length > 0 && !!formState.value.type
);

const selectType = (type) => {
  formState.value.type = type;
  
  if (formRef.value && formRef.value.validate) {
    // Validate name field then submit on success
    formRef.value
      .validate()
      .then(() => {
        submit();
      })
      .catch(() => {
        console.error("Please enter a valid property name");
      });
  } else {
    if (trimmedName.value) submit();
  }
};

const trySubmit = () => {
  if (canSubmit.value) submit();
};

const submit = async (relatedObjects = null) => {
  if (!canSubmit.value || submitting.value) return;
  submitting.value = true;
  try {
    const isFirstProperty =
      isTickets.value || isTasks.value
        ? false
        : await checkIfFirstTextProperty(
            props.entityType,
            formState.value.type,
            CRMService,
            excludedFormFields,
            props.service,
          );

    const payload = {
      name: trimmedName.value,
      type: formState.value.type,
      created_by_id: Number(localStorage.getItem("userId")) || 1,
    };

    if (formState.value.type === ATTRIBUTE_TYPES.MULTI_SELECT) {
      payload.is_multi_value = true;
    }

    // Add related_objects for record type
    if (formState.value.type === ATTRIBUTE_TYPES.RECORD && relatedObjects) {
      payload.related_objects = relatedObjects;
    }

    let response;
    if (isTickets.value) {
      response = await TicketsService.addSchemaAttribute(payload);
    } else if (isTasks.value) {
      if (!props.projectId) {
        message.error("Project ID is required for adding task attributes");
        return;
      }
      response = await ProjectService.tasks.addTaskSchemaAttribute(
        props.projectId,
        payload
      );
    } else {
      response = await CRMService.createSchemaObjectAttribute(
        props.entityType,
        payload,
        props.service,
      );
    }

    if (response?.success) {
      const entitySlug = isTickets.value
        ? entityTypes.TICKETS
        : isTasks.value
          ? entityTypes.TASKS
          : props.entityType;
      await store.dispatch("schema/refreshSchema", {
        slug: entitySlug,
        projectId: isTasks.value ? props.projectId : null,
        service: props.service || null,
      });

      if (isFirstProperty && response.attribute) {
        const attributeSlug =
          response.attribute.slug ||
          generateSlugFromName(response.attribute.name);
        if (attributeSlug) {
          await setPropertyAsPrimary(
            props.entityType,
            attributeSlug,
            CRMService,
            response.attribute,
            props.service,
          );
        }
      }

      if (response.attribute) {
        try {
          await addNewAttributeToActiveView(
            props.entityType,
            response.attribute.id,
            props.service,
          );
        } catch (error) {
          console.error("Error adding property to active view:", error);
        }
      }

      message.success("Property created successfully");
      emit("property-added", {
        property: response.attribute,
        shouldRefresh: true,
      });
      resetForm();
      dropdownVisible.value = false;
    } else {
      const errorMsg = response?.error?.startsWith("attribute with name")
        ? `Column with name "${trimmedName.value}" already exists`
        : "Failed to create property";
      message.error(errorMsg);
    }
  } catch (error) {
    console.error("Error creating property:", error);
    message.error("Failed to create property");
  } finally {
    submitting.value = false;
  }
};

const resetForm = () => {
  formState.value = { name: "", type: "" };
};

const handleDropdownChange = (isOpen) => {
  if (!isOpen) {
    resetForm();
    if (formRef.value) {
      formRef.value.clearValidate();
    }
  }
};

const nameRules = [
  { required: true, message: "Property name is required" },
  {
    validator: (_rule, value) => {
      if (!value) return Promise.resolve();

      if (value.trim().length === 0) {
        return Promise.reject("Property name cannot be empty or spaces only");
      }

      if (value.length > 50) {
        return Promise.reject(
          "Character limit exceeded. Maximum 50 characters allowed."
        );
      }

      const validNameRegex = /^[a-zA-Z0-9\s.,!?()-]+$/;
      if (!validNameRegex.test(value)) {
        return Promise.reject(
          "Only alphanumeric, spaces and basic punctuation allowed."
        );
      }

      if (formState.value.type === ATTRIBUTE_TYPES.SELECT_TIMESTAMP && value.trim()) {
        const schema = store.getters["schema/getSchemaBySlug"](
          props.entityType,
          isTasks.value ? props.projectId : null
        );
        
        if (schema?.attributes) {
          const normalizedNewName = value.trim().toLowerCase();
          const isDuplicate = schema.attributes.some((attr) => {
            const existingName = attr.name?.trim().toLowerCase();
            return existingName === normalizedNewName;
          });

          if (isDuplicate) {
            return Promise.reject("A property with this name already exists. Please choose a different name.");
          }
        }
      }

      return Promise.resolve();
    },
    trigger: ["blur", "change"],
  },
];
</script>

<style>
.add_property_dropdown_content {
  background: var(--dropdown-bg);
  border-radius: 8px;
  box-shadow: var(--dropdown-boxshadow);
  max-height: 400px;
  overflow-y: auto;
}
</style>
